import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MeetingLocation,
  MeetingSource,
  MeetingDetailInterface,
  GuestGetBookedMeetingResponseV2,
  GuestGetBookedMeetingResponseV2Interface,
} from '@vendasta/meetings';
import { combineLatest, Observable, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { getTimezone } from '../util';
import { isValidTimezone } from '@vendasta/galaxy/utility/timezone';

const GOOGLE_MAPS_SEARCH_URL =
  'https://www.google.com/maps/search/?api=1&query=';

const HELP_LINK_BY_MEETING_SOURCE: Partial<Record<MeetingSource, string>> = {
  [MeetingSource.MEETING_SOURCE_GOOGLE_MEET]:
    'https://workspace.google.com/products/meet/',
  [MeetingSource.MEETING_SOURCE_MICROSOFT]:
    'https://support.microsoft.com/en-us/office/join-a-meeting-in-microsoft-teams-1613bb53-f3fa-431e-85a9-d6a91e3468c9',
  [MeetingSource.MEETING_SOURCE_ZOOM]:
    'https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0060732',
};

type MeetingInfoVm = {
  meetingInfo: GuestGetBookedMeetingResponseV2;
  hostNamesText: string;
  dateText: string;
  timeRangeText: string;
  durationText: string;
  isVideo: boolean;
  isInPerson: boolean;
  meetingSourceText: string;
  timezoneZoneText: string;
  hasMeetingLink: boolean;
  hasCalendarLinks: boolean;
  meetingLinkFallbackText: string;
  calendarLinkFallbackText: string;
  businessLogo: string;
  displayTitle: string;
  isBatch: boolean;
  meetings: BatchMeetingVm[];
  batchLocationsText: string;
  batchGoogleMapsLocation: string;
};

type BatchMeetingVm = MeetingDetailInterface & {
  eventTypeName: string;
  meetingSourceText: string;
};

type GuestBookedMeetingV2WithTimezone = GuestGetBookedMeetingResponseV2 & {
  timezone?: string | { zone?: string };
  timeZone?: { id?: string };
};

@Component({
  selector: 'app-meeting-info-page',
  templateUrl: './meeting-info.component.html',
  styleUrls: ['./meeting-info.component.scss'],
  standalone: false,
})
export class MeetingInfoPageComponent implements OnInit {
  meetingId!: string;
  meetingInfo$: Observable<GuestGetBookedMeetingResponseV2>;
  meetingInfoVm$: Observable<MeetingInfoVm | null>;

  isRedirecting = false;
  redirectSecondsRemaining = 0;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly bookMeNowStoreService: BookMeNowStoreService
  ) {}

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.params.meetingId;
    // this.route.snapshot.paramMap.get('meetingId')!;
    this.meetingInfo$ = this.bookMeNowStoreService
      .getGuestBookedMeetingV2({ id: this.meetingId })
      .pipe(
        map(
          (
            meetingInfo: GuestGetBookedMeetingResponseV2 | null
          ): GuestGetBookedMeetingResponseV2 | null => {
            if (!meetingInfo) {
              return meetingInfo;
            }

            const { isCancelled } = meetingInfo;

            if (typeof isCancelled === 'undefined') {
              return meetingInfo;
            }

            if (isCancelled) {
              this.router.navigate(
                ['/cancel-info', this.meetingId, 'cancelled'],
                {
                  queryParamsHandling: 'preserve',
                }
              );
              return null;
            }

            return meetingInfo;
          }
        ),
        catchError((err) => {
          this.bookMeNowStoreService.handleMeetingError(err, 'info');
          return of(null);
        })
      );

    this.meetingInfoVm$ = combineLatest([
      this.meetingInfo$,
      this.bookMeNowStoreService.notificationType$,
    ]).pipe(
      map(([meetingInfo]) => {
        if (!meetingInfo) {
          return null;
        }

        // Some fields on GuestGetBookedMeetingResponseV2 come from internal models; keep this defensive.
        const meetingInfoWithTimezone =
          meetingInfo as GuestBookedMeetingV2WithTimezone;
        const timezoneRegion =
          typeof meetingInfoWithTimezone.timezone === 'string'
            ? meetingInfoWithTimezone.timezone
            : meetingInfoWithTimezone.timeZone?.id;
        const timezoneZoneText =
          typeof meetingInfoWithTimezone.timezone === 'object'
            ? meetingInfoWithTimezone.timezone?.zone || ''
            : '';

        const isVideo = this.isVideoMeeting(meetingInfo.meetingLocationType);
        const isInPerson = this.isInPersonMeeting(
          meetingInfo.meetingLocationType
        );

        // Determine fallback messages based on notification type using $localize
        const isEmailNotification = false;
        const meetingLinkFallbackText = isEmailNotification
          ? $localize`:@@MEETING_INFO.MEETING_DETAILS_EMAILED_SHORTLY:Meeting details will be emailed shortly`
          : $localize`:@@MEETING_INFO.MEETING_DETAILS_MESSAGED_SHORTLY:Meeting details will be messaged shortly`;
        const calendarLinkFallbackText = isEmailNotification
          ? $localize`:@@MEETING_INFO.CALENDAR_LINK_EMAILED_SHORTLY:Calendar link will be emailed shortly`
          : $localize`:@@MEETING_INFO.CALENDAR_LINK_MESSAGED_SHORTLY:Calendar link will be messaged shortly`;

        const responseWithBatch =
          meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface;
        const isBatch =
          !!responseWithBatch.bookingGroupId &&
          !!responseWithBatch.meetings?.length;
        const meetings: MeetingDetailInterface[] =
          responseWithBatch.meetings || [];

        // For batch: check if any meeting has video/in-person
        let batchIsVideo = isVideo;
        let batchIsInPerson = isInPerson;
        let batchHasMeetingLink = !!meetingInfo.meetingSourceLink;
        if (isBatch) {
          batchIsVideo = meetings.some((m) =>
            this.isVideoMeeting(m.meetingLocationType)
          );
          batchIsInPerson = meetings.some((m) =>
            this.isInPersonMeeting(m.meetingLocationType)
          );
          batchHasMeetingLink = meetings.some((m) => !!m.meetingSourceLink);
        }

        const extractEventTypeName = (title: string): string => {
          const firstDash = title.indexOf(' - ');
          return firstDash >= 0 ? title.substring(firstDash + 3) : title;
        };

        let displayTitle: string;
        if (isBatch) {
          const eventTypeNames = meetings.map((m) =>
            extractEventTypeName(m.title || '')
          );
          displayTitle = eventTypeNames.join(', ');
        } else {
          displayTitle = extractEventTypeName(meetingInfo.title || '');
        }

        // Batch locations: comma-separated distinct locations
        let batchLocationsText = '';
        let batchGoogleMapsLocation = '';
        if (isBatch) {
          const distinctLocations = [
            ...new Set(
              meetings.map((m) => m.location).filter((l): l is string => !!l)
            ),
          ];
          batchLocationsText = distinctLocations.join(', ');
          batchGoogleMapsLocation = distinctLocations[0] || '';
        }

        return {
          meetingInfo,
          hostNamesText: this.formatNames(meetingInfo.hostUserNames || []),
          dateText: this.formatDate(meetingInfo.startTime),
          timeRangeText: this.formatTimeRange(
            meetingInfo.startTime,
            meetingInfo.endTime,
            timezoneRegion
          ),
          durationText: this.formatDuration(
            meetingInfo.startTime,
            meetingInfo.endTime
          ),
          isVideo: isBatch ? batchIsVideo : isVideo,
          isInPerson: isBatch ? batchIsInPerson : isInPerson,
          meetingSourceText: this.getMeetingSource(meetingInfo.meetingSource),
          timezoneZoneText,
          hasMeetingLink: isBatch
            ? batchHasMeetingLink
            : !!meetingInfo.meetingSourceLink,
          hasCalendarLinks:
            !!meetingInfo.googleCalendarLink ||
            !!meetingInfo.outlookCalendarLink,
          meetingLinkFallbackText,
          calendarLinkFallbackText,
          businessLogo: meetingInfo.businessLogo || '',
          displayTitle,
          isBatch,
          meetings: meetings.map((m) => ({
            ...m,
            eventTypeName: extractEventTypeName(m.title || ''),
            meetingSourceText: this.getMeetingSource(m.meetingSource),
          })),
          batchLocationsText,
          batchGoogleMapsLocation,
        };
      }),
      shareReplay(1)
    );

    this.setupPostBookingRedirect();
  }

  private setupPostBookingRedirect(): void {
    this.meetingInfoVm$
      .pipe(
        map((vm) => vm?.meetingInfo),
        filter(
          (meeting): meeting is GuestGetBookedMeetingResponseV2 =>
            Boolean(meeting?.redirectUrl)
        ),
        take(1),
        switchMap((meeting) => {
          const totalSeconds = Math.max(meeting.redirectDelaySeconds ?? 0, 0);
          this.isRedirecting = true;
          this.redirectSecondsRemaining = totalSeconds;
          return timer(0, 1000).pipe(
            map((elapsed) => totalSeconds - elapsed),
            takeWhile((remaining) => remaining >= 0),
            tap((remaining) => {
              this.redirectSecondsRemaining = remaining;
              if (remaining <= 0) {
                this.navigateToRedirectUrl(meeting.redirectUrl);
              }
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private navigateToRedirectUrl(url: string): void {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return;
    }
    if (parsed.protocol !== 'https:') {
      return;
    }
    const target: Window = window.top ?? window;
    target.location.href = url;
  }

  extractEventTypeName(title: string): string {
    const safeTitle = title || '';
    const firstDash = safeTitle.indexOf(' - ');
    return firstDash >= 0 ? safeTitle.substring(firstDash + 3) : safeTitle;
  }

  openLinkInNewTab(url: string) {
    window.open(url, '_blank').focus();
  }

  isVideoMeeting(locationType: MeetingLocation): boolean {
    if (
      locationType === MeetingLocation.VIDEO_MEETING ||
      locationType === MeetingLocation.HYBRID_MEETING ||
      locationType === undefined
    ) {
      return true;
    }
    return false;
  }

  isInPersonMeeting(locationType: MeetingLocation): boolean {
    if (
      locationType === MeetingLocation.IN_PERSON_MEETING ||
      locationType === MeetingLocation.HYBRID_MEETING
    ) {
      return true;
    }
    return false;
  }

  getMeetingSource(source: MeetingSource): string {
    if (source === MeetingSource.MEETING_SOURCE_ZOOM) {
      return 'Zoom';
    }
    if (source === MeetingSource.MEETING_SOURCE_MICROSOFT) {
      return 'Microsoft';
    }
    return 'Google';
  }

  navigateToHelpLink(source: MeetingSource) {
    const url = HELP_LINK_BY_MEETING_SOURCE[source];
    if (!url) {
      return;
    }
    this.openLinkInNewTab(url);
  }

  navigateToGoogleMaps(location: string): void {
    if (!location || location.trim() === '') {
      return;
    }
    // Encode the location string for URL
    const encodedLocation = encodeURIComponent(location.trim());
    // Construct Google Maps search URL
    this.openLinkInNewTab(`${GOOGLE_MAPS_SEARCH_URL}${encodedLocation}`);
  }

  formatNames(names: string[] = []): string {
    if (names.length <= 1) {
      return names[0] ?? '';
    }

    const last = names[names.length - 1];
    const rest = names.slice(0, -1).join(', ');

    return `${rest} and ${last}`;
  }

  formatDate(date: string | Date): string {
    if (!date) {
      return '';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    const formatted = dateObj.toLocaleDateString('en-US', options);
    // Format: "Thursday, December 15, 2025" -> "Thursday - December 15, 2025"
    return formatted.replace(', ', ' - ').replace(',', '');
  }

  formatTimeRange(
    startTime: string | Date,
    endTime: string | Date,
    timezone?: string
  ): string {
    if (!startTime || !endTime) {
      return '';
    }

    const startDate =
      typeof startTime === 'string' ? new Date(startTime) : startTime;
    const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;
    const rawTz = timezone || getTimezone();
    const timezoneRegion = isValidTimezone(rawTz) ? rawTz : 'UTC';

    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezoneRegion,
    };

    const startTimeFormatted = startDate.toLocaleTimeString(
      'en-US',
      timeOptions
    );
    const endTimeFormatted = endDate.toLocaleTimeString('en-US', timeOptions);

    const timezoneAbbr =
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneRegion,
        timeZoneName: 'short',
      })
        .formatToParts(startDate)
        .find((part) => part.type === 'timeZoneName')?.value || '';

    return `${startTimeFormatted} to ${endTimeFormatted}, ${timezoneAbbr} (${timezoneRegion})`;
  }

  formatDuration(startTime: string | Date, endTime: string | Date): string {
    if (!startTime || !endTime) {
      return '';
    }

    const startDate =
      typeof startTime === 'string' ? new Date(startTime) : startTime;
    const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;

    // Calculate difference in milliseconds
    const diffMs = endDate.getTime() - startDate.getTime();

    // Convert to minutes
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    // Calculate hours and remaining minutes
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    // Format: "15min" if hours are 0, "2hr" if minutes are 0, or "1hr 15min"
    if (hours === 0) {
      return `${minutes} min`;
    }

    if (minutes === 0) {
      return `${hours} hr`;
    }

    return `${hours} hr ${minutes} min`;
  }
}
